import React, { useEffect, useRef, useState } from 'react';
import { API_URLS } from '../apiUrl';
import PropertyTypeDropdown from '../pages/customHooks/propertyTypeDropdown';
import PropertyTypeDescriptionDropdown from '../pages/customHooks/propertyTypeDescriptionDropdown';
import axiosInstance from '../components/axiosInstance';

const UseOfPropertyEdit = ({ isOpen, closeModal, useOfPropertyName, useOfPropertyId, propertyTypeDescriptionId, propertyTypeId, propertyTypeName }: any) => {

    const [formData, setFormData] = useState<any>({
        use_of_property: "",
        property_type_id: propertyTypeId || "",
        property_type_description_id: propertyTypeDescriptionId || "",
        use_of_property_id: useOfPropertyId
    });

    const [errors, setErrors] = useState<any>({});
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [dataExistMessage, setDataExistMessage] = useState<string>("");
    const [propertyTypeName1, setPropertyTypeName1] = useState<string>("");

    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (isOpen) {
            setFormData((prevState: any) => ({
                ...prevState,
                use_of_property_id: useOfPropertyId,
                use_of_property: useOfPropertyName || "",
                property_type_id: propertyTypeId || "",
                property_type_description_id: propertyTypeDescriptionId || "",
            }));
        }
    }, [isOpen, useOfPropertyName, useOfPropertyId, propertyTypeId, propertyTypeDescriptionId]);

    useEffect(() => {
        if (isOpen) {
            setPropertyTypeName1(propertyTypeName);
        }
    }, [isOpen, propertyTypeName]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | any
    ) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
        
        if (!formData.use_of_property.trim()) {
            newErrors.description_name = "Use Of Property is required";
        } else if (!/^[a-zA-Z\s]*$/.test(formData.use_of_property)) {
            newErrors.description_name = "Use Of Property can only contain letters and spaces";
        }
    
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const dataToSubmit = {
            ...formData,
            property_type_id: formData.property_type_id || propertyTypeId,
            property_type_description_id: formData.property_type_description_id || propertyTypeDescriptionId,
        };

        if (validateForm()) {
            try {
                const response = await axiosInstance.put(`${API_URLS.UseOfPropertyUpdate}`, dataToSubmit);

                if (response.data.message) {
                    setSuccessMessage(`${response.data.message}`);
                    setTimeout(() => {
                        setSuccessMessage("");
                        closeModal();
                    }, 5000);
                }

                setErrors({});
            } catch (error) {
                setDataExistMessage("Use of Property already exists. Please choose a different name.");
                setTimeout(() => {
                    setDataExistMessage("");
                    closeModal();
                }, 5000);
            }
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (formRef.current && !formRef.current.contains(e.target as Node)) {
            setErrors({});
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const [, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();
    const [, isDropdownOpenPropertyDescription, , propertyDescriptions, loadingPropertyDescription, setIsDropdownOpenPropertyDescription] = PropertyTypeDescriptionDropdown(formData.property_type_id);

    const handlePropertyTypeSelect = (selectedValue: string) => {
        const selectedPropertyType = propertyTypes?.find((type: any) => type.type_name === selectedValue);
        if (selectedPropertyType) {
            setFormData((prevState: any) => ({
                ...prevState,
                property_type_id: selectedPropertyType.type_primary_id,
            }));
        }
        setIsDropdownOpen(false);
    };

    const handlePropertyTypeDescriptionSelect = (e: any) => {
        const { value, checked } = e.target;

        setFormData((prevFormData: any) => {
            const currentDescriptionIds = prevFormData.property_type_description_id || "";
            const newSelections = currentDescriptionIds ? currentDescriptionIds.split(",") : [];

            if (checked) {
                if (!newSelections.includes(value)) {
                    newSelections.push(value);
                }
            } else {
                const index = newSelections.indexOf(value);
                if (index > -1) {
                    newSelections.splice(index, 1);
                }
            }

            return {
                ...prevFormData,
                property_type_description_id: newSelections.join(","),
            };
        });
    };

    const dropdownRefPropertyType = useRef<any>(null);
    const dropdownRefPropertyDescription = useRef<any>(null);

    useEffect(() => {
        const handleClickOutsideDropdown = (event: MouseEvent) => {
            if (dropdownRefPropertyType.current && !dropdownRefPropertyType.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }

            if (dropdownRefPropertyDescription.current && !dropdownRefPropertyDescription.current.contains(event.target as Node)) {
                setIsDropdownOpenPropertyDescription(false);
            }
        };

        document.addEventListener("click", handleClickOutsideDropdown);

        return () => {
            document.removeEventListener("click", handleClickOutsideDropdown);
        };
    }, []);

    const toggleDropdown = (
        e: React.MouseEvent<HTMLButtonElement>,
        setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        e.preventDefault();
        setDropdownOpen((prev) => !prev);
    };

    useEffect(() => {
        if (isOpen) {
            setFormData((prevState: any) => ({
                ...prevState,
                use_of_property: useOfPropertyName || "",
            }));
        }
    }, [isOpen, useOfPropertyName]);

    if (!isOpen) return null;

    const handleOutsideClick = (e: any) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    const isDescriptionSelected = (descriptionId: number): boolean => {
        const ids = formData.property_type_description_id || "";
        return ids.split(",").includes(descriptionId.toString());
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center" onClick={handleOutsideClick}>
            <div className="bg-white p-6 rounded-lg shadow-lg w-[740px] relative" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">Edit Use Of Property</h2>

                <form ref={formRef} onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {/* Property Type Dropdown */}
                        <div>
                            <label htmlFor="property_type" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Property Type <span className="text-red-600">*</span></label>
                            <div className="relative" ref={dropdownRefPropertyType}>
                                <button
                                    onClick={(e) => toggleDropdown(e, setIsDropdownOpen)}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                                    type="button"
                                >
                                    {propertyTypes?.find((type: any) => type.type_primary_id === formData.property_type_id)?.type_name || propertyTypeName1 || "Select Property Type"}
                                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                                </button>

                                {isDropdownOpen && (
                                    <select
                                        id="property_type"
                                        name="property_type"
                                        value={formData.property_type_id}
                                        onChange={(e) => handlePropertyTypeSelect(e.target.value)}
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full"
                                        size={10}
                                    >
                                        {propertyTypes.map((property: any) => (
                                            <option key={property.type_primary_id} value={property.type_name}>
                                                {property.type_name}
                                            </option>
                                        ))}
                                        {loading && <option>Loading...</option>}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Property Type Description Dropdown */}
                        <div>
                            <label htmlFor="property_type_description" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Property Type Description <span className="text-red-600">*</span></label>
                            <div className="relative" ref={dropdownRefPropertyDescription}>
                                <button
                                    onClick={(e) => toggleDropdown(e, setIsDropdownOpenPropertyDescription)}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                                    type="button"
                                >
                                    {formData.property_type_description_id && formData.property_type_description_id.length > 0
                                        ? propertyDescriptions
                                            .filter((desc: any) => formData.property_type_description_id.split(",").includes(desc.id.toString()))
                                            .map((desc: any) => desc.type_description_name)
                                            .join(", ")
                                        : "Select Property Type Description"}
                                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                                </button>

                                {isDropdownOpenPropertyDescription && (
                                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[175px] overflow-y-auto">
                                        {propertyDescriptions.map((property: any) => (
                                            <div key={property.id} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id={`property_${property.id}`}
                                                    value={property.id}
                                                    checked={isDescriptionSelected(property.id)}
                                                    onChange={(e) => handlePropertyTypeDescriptionSelect(e)}
                                                    className="text-blue-600"
                                                />
                                                <label htmlFor={`property_${property.id}`} className="text-sm text-gray-900 dark:text-white">
                                                    {property.type_description_name}
                                                </label>
                                            </div>
                                        ))}
                                        {loadingPropertyDescription && <p>Loading...</p>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Use Of Property */}
                        <div>
                            <label htmlFor="use_of_property" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Use Of Property <span className="text-red-600">*</span></label>
                            <input
                                type="text"
                                id="use_of_property"
                                name="use_of_property"
                                value={formData.use_of_property}
                                onChange={handleChange}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            />
                            {errors.description_name && (
                                <p className="text-sm text-red-600 mt-1">{errors.description_name}</p>
                            )}
                        </div>

                        <div className='sm:col-span-2'>
                            <button type="submit" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                                Update
                            </button>
                        </div>
                    </div>
                </form>

                {successMessage && (
                    <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
                        <span>{successMessage}</span>
                        <button
                            onClick={() => setSuccessMessage('')}
                            className="ml-4 text-white font-bold text-lg focus:outline-none"
                        >
                            &times;
                        </button>
                    </div>
                )}

                {dataExistMessage && (
                    <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
                        <span>{dataExistMessage}</span>
                        <button
                            onClick={() => setDataExistMessage('')}
                            className="ml-4 text-white font-bold text-lg focus:outline-none"
                        >
                            &times;
                        </button>
                    </div>
                )}

                <button
                    onClick={closeModal}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                >
                    <i className="ri-close-line"></i>
                </button>
            </div>
        </div>
    );
};

export default UseOfPropertyEdit;